import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateUser, useAssignRole, useImportDirectoryUser } from "@/hooks/rbac";
import { useRoles } from "@/hooks/rbac/useRoles";
import { useIdentityProviders } from "@/hooks/useIdentityProviders";
import { useDirectoryUserSearch, DirectoryUser } from "@/hooks/useDirectoryUserSearch";
import { Loader2, UserPlus, Building, AlertCircle, UserX, ChevronLeft, Eye, EyeOff, IdCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { toast } from "sonner";

const localUserSchema = z.object({
  email: z.string().trim().email("Invalid email format").max(255, "Email must be less than 255 characters"),
  password: z.string().min(8, "Password must be at least 8 characters").max(128, "Password must be less than 128 characters"),
  firstName: z.string().trim().min(1, "First name is required").max(100, "First name must be less than 100 characters"),
  lastName: z.string().trim().min(1, "Last name is required").max(100, "Last name must be less than 100 characters"),
});

interface CreateUserWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateUserWizardDialog({ open, onOpenChange }: CreateUserWizardDialogProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState<'local' | 'directory' | null>(null);
  
  // Local user state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Directory import state
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDirectoryUser, setSelectedDirectoryUser] = useState<DirectoryUser | null>(null);
  
  // Common state
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  
  const { mutate: createUser, isPending: isCreatingUser } = useCreateUser();
  const { mutate: assignRole } = useAssignRole();
  const { mutate: importUser, isPending: isImportingUser } = useImportDirectoryUser();
  const { data: roles = [] } = useRoles();
  const { data: providers = [], isLoading: isLoadingProviders } = useIdentityProviders();
  const { data: searchResults = [], isLoading: isSearching } = useDirectoryUserSearch(selectedProvider, searchQuery);
  
  const isPending = isCreatingUser || isImportingUser;
  
  useEffect(() => {
    if (!open) {
      // Reset all state when dialog closes
      setTimeout(() => {
        setStep(0);
        setUserType(null);
        setEmail("");
        setPassword("");
        setFirstName("");
        setLastName("");
        setSelectedProvider(null);
        setSearchQuery("");
        setSelectedDirectoryUser(null);
        setSelectedRoles([]);
        setShowPassword(false);
      }, 200);
    }
  }, [open]);
  
  const handleRoleToggle = (roleId: string) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };
  
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleLocalUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    
    const result = localUserSchema.safeParse({ email, password, firstName, lastName });
    if (!result.success) {
      const msg = result.error.errors[0].message;
      setValidationError(msg);
      toast.error(msg);
      return;
    }
    
    createUser({
      email,
      password,
      firstName,
      lastName
    }, {
      onSuccess: async (data) => {
        if (selectedRoles.length > 0) {
          const newUserId = (data as any)?.user?.id || (data as any)?.id;
          if (newUserId) {
            for (const roleId of selectedRoles) {
              try {
                await new Promise<void>((resolve, reject) => {
                  assignRole({ userId: newUserId, roleId }, {
                    onSuccess: () => resolve(),
                    onError: (error) => reject(error)
                  });
                });
              } catch (error) {
                console.error(`Failed to assign role ${roleId}:`, error);
              }
            }
          }
        }
        
        setTimeout(() => {
          onOpenChange(false);
        }, 500);
      }
    });
  };
  
  const handleDirectoryUserImport = () => {
    if (!selectedDirectoryUser) return;
    
    importUser({
      directoryUserId: selectedDirectoryUser.id,
      roleIds: selectedRoles
    }, {
      onSuccess: () => {
        setTimeout(() => {
          onOpenChange(false);
        }, 500);
      }
    });
  };
  
  const handleBack = () => {
    if (step === 1 && userType === 'directory') {
      setSearchQuery("");
      setSelectedDirectoryUser(null);
    }
    setStep(Math.max(0, step - 1));
  };
  
  const canProceedToRoles = () => {
    if (userType === 'local') {
      return email && password && firstName && lastName;
    } else if (userType === 'directory') {
      return selectedDirectoryUser !== null;
    }
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-semibold">
            {step === 0 ? "Add User" : userType === 'local' ? "Create Local User" : "Import from Directory"}
          </DialogTitle>
          <DialogDescription>
            {step === 0 && "Choose how to add a new user to the console"}
            {step === 1 && userType === 'local' && "Enter user details and assign roles"}
            {step === 1 && userType === 'directory' && "Search and select a user from your directory"}
            {step === 2 && "Assign roles to the user"}
          </DialogDescription>
        </DialogHeader>
        
        {/* Step 0: User Type Selection */}
        {step === 0 && (
          <div className="grid grid-cols-2 gap-4">
            <Card 
              disableHover
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => { setUserType('local'); setStep(1); }}
            >
              <CardContent className="pt-6 text-center">
                <UserPlus className="w-12 h-12 mb-4 text-primary mx-auto" />
                <h3 className="font-semibold mb-2">Local User</h3>
                <p className="text-sm text-muted-foreground">
                  Create a new console user with email and password
                </p>
              </CardContent>
            </Card>
            
            <Card 
              disableHover
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => { setUserType('directory'); setStep(1); }}
            >
              <CardContent className="pt-6 text-center">
                <IdCard className="w-12 h-12 mb-4 text-primary mx-auto" />
                <h3 className="font-semibold mb-2">Import from Directory</h3>
                <p className="text-sm text-muted-foreground">
                  Search and import users from identity providers
                </p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Step 1: Local User Form */}
        {step === 1 && userType === 'local' && (
          <form onSubmit={handleLocalUserSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input 
                    id="firstName" 
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input 
                    id="lastName" 
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="email">Email Address</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="user@company.com"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input 
                    id="password" 
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Minimum 8 characters"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <Separator />
            
            {/* Roles Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Role Assignment</h3>
              
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {roles.map((role) => (
                  <div key={role.id} className="flex items-start space-x-3">
                    <Checkbox
                      id={role.id}
                      checked={selectedRoles.includes(role.id)}
                      onCheckedChange={() => handleRoleToggle(role.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <Label 
                        htmlFor={role.id} 
                        className="text-sm font-medium cursor-pointer"
                      >
                        {role.name}
                      </Label>
                      {role.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {role.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleBack}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                type="submit" 
                disabled={isPending || !email || !password || !firstName || !lastName}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : "Create User"}
              </Button>
            </div>
          </form>
        )}
        
        {/* Step 1: Directory Import Flow */}
        {step === 1 && userType === 'directory' && (
          <div className="space-y-6">
            {isLoadingProviders ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : providers.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No Identity Providers</AlertTitle>
                <AlertDescription>
                  No active identity providers found. Please configure an identity provider first.
                  <Button 
                    variant="link" 
                    className="p-0 h-auto ml-1" 
                    onClick={() => navigate('/settings?category=identity')}
                  >
                    Go to Integrations
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Select Identity Provider</Label>
                  <Select value={selectedProvider || undefined} onValueChange={setSelectedProvider}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(provider => (
                        <SelectItem key={provider.id} value={provider.id}>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{provider.type}</Badge>
                            {provider.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedProvider && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Search Users</Label>
                      <Input
                        placeholder="Search by username or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery.length > 0 && searchQuery.length < 2 && (
                        <p className="text-xs text-muted-foreground">Type at least 2 characters to search</p>
                      )}
                    </div>
                    
                    {isSearching && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    
                    {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                      <div className="text-center py-8 text-muted-foreground">
                        <UserX className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No users found matching "{searchQuery}"</p>
                      </div>
                    )}
                    
                    {searchResults.length > 0 && (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {searchResults.map(user => (
                          <Card
                            key={user.id}
                            disableHover
                            className={cn(
                              "cursor-pointer hover:border-primary transition-colors",
                              selectedDirectoryUser?.id === user.id && "border-primary bg-primary/5"
                            )}
                            onClick={() => setSelectedDirectoryUser(user)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">
                                    {user.first_name && user.last_name 
                                      ? `${user.first_name} ${user.last_name}` 
                                      : user.username}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{user.username}</p>
                                  {user.email && (
                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                  )}
                                </div>
                                {user.department && (
                                  <Badge variant="secondary">{user.department}</Badge>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {selectedDirectoryUser && (
                  <>
                    <Separator />
                    
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Role Assignment</h3>
                      
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        {roles.map((role) => (
                          <div key={role.id} className="flex items-start space-x-3">
                            <Checkbox
                              id={role.id}
                              checked={selectedRoles.includes(role.id)}
                              onCheckedChange={() => handleRoleToggle(role.id)}
                              className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <Label 
                                htmlFor={role.id} 
                                className="text-sm font-medium cursor-pointer"
                              >
                                {role.name}
                              </Label>
                              {role.description && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {role.description}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            
            <div className="flex justify-between pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleBack}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={handleDirectoryUserImport}
                disabled={isPending || !selectedDirectoryUser}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : "Import User"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
